import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '../auth/useAccessToken';
import { apiFetch } from './client';
import type { ApiError } from './client';

/**
 * Revenue dashboard API layer — types + TanStack Query hooks for the
 * `/api/v1/revenue/*` surface (UP-MVP wave-1, task 8). Field shapes mirror
 * the backend records in `src/Route.Route/Revenue/RevenueModels.cs`
 * verbatim (System.Text.Json emits camelCase by default).
 *
 * Real UI (portfolio dashboard, site drill-down, machine drill-down) lands
 * in tasks 9–11; this file only ships the data layer + route/nav wiring.
 */

/** Accepted `period` query tokens. `custom` requires `from` + `to`. */
export type RevenuePeriodToken = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'mtd' | 'custom';

export type RevenueTotals = {
  turnoverCents: number;
  totalOutCents: number;
  jackpotsCents: number;
  netWinCents: number;
  holdPct: number | null;
};

export type TrendPoint = {
  gamingDay: string; // YYYY-MM-DD
  turnoverCents: number;
  netWinCents: number;
  // True when this gaming day contains a suspect reading. The money is STILL
  // included in the totals/trend (include-and-flag model) — this only drives the
  // red review marker on the point. (Confirm/amend resolution is future work.)
  suspect: boolean;
};

export type SiteRankRow = {
  siteId: string;
  siteName: string;
  netWinCents: number;
  turnoverCents: number;
  holdPct: number | null;
  machinesOnline: number | null;
  machinesTotal: number | null;
  // Per-site daily series (ascending gaming_day) over the current window, for the
  // per-row trend sparkline. Empty / single-point when the site has ≤1 day.
  trend: TrendPoint[];
};

// One row PER MACHINE CONFIG (game version), not per cabinet: a RAM-reset /
// config change mints a new version and reporting treats it as a new machine —
// V1 and V2 money is never summed into one row, even for the same cabinet.
export type MachineRevenueRow = {
  machineId: string;
  versionId: string;
  versionNo: number;
  machineLabel: string;
  // Nullable — route.machine.game_name is optional; display falls back to
  // machineLabel (the asset number) when absent.
  gameName: string | null;
  siteId: string;
  siteName: string;
  turnoverCents: number;
  totalOutCents: number;
  jackpotsCents: number;
  netWinCents: number;
  holdPct: number | null;
};

// Reason enum values the backend emits today — kept as a plain string too
// so an unrecognised future value doesn't blow up rendering.
export type AffectedMachineReason = 'baseline_day' | 'suspect_day' | 'unreviewed_version' | 'unavailable' | string;

export type AffectedMachine = {
  machineId: string;
  machineLabel: string;
  siteId: string;
  reason: AffectedMachineReason;
};

export type DataState = {
  currentPeriod: boolean;
  provisional: boolean;
  affectedCount: number;
  generatedAt: string;
};

export type RevenuePortfolio = {
  totals: RevenueTotals;
  // Totals for the prior window of the same length immediately preceding the
  // current one; null when no prior window/data exists. Drives the vs-prior deltas.
  priorTotals: RevenueTotals | null;
  machinesOnline: number | null;
  machinesTotal: number | null;
  trend: TrendPoint[];
  siteRanking: SiteRankRow[];
  topMachines: MachineRevenueRow[];
  needsAttention: MachineRevenueRow[];
  affected: AffectedMachine[];
  dataState: DataState;
  periodToken: string;
  from: string | null;
  to: string | null;
  scopeDescription: string;
};

export type RevenueSite = {
  siteId: string;
  siteName: string;
  totals: RevenueTotals;
  priorTotals: RevenueTotals | null;
  machinesOnline: number | null;
  machinesTotal: number | null;
  machinesStale: number | null;
  trend: TrendPoint[];
  machines: MachineRevenueRow[];
  affected: AffectedMachine[];
  dataState: DataState;
  periodToken: string;
  from: string | null;
  to: string | null;
  scopeDescription: string;
};

export type RevenueMachineCompleteness = 'complete' | 'incomplete' | 'unavailable';

export type RevenueMachine = {
  machineId: string;
  machineLabel: string;
  siteId: string;
  siteName: string;
  // The CONFIG this drill covers (current game version by default; a prior one
  // via ?versionId). totals/trend are for THIS config only — never a
  // cross-config sum.
  versionId: string | null;
  versionNo: number | null;
  gameName: string | null;
  denominationCents: number | null;
  rtpBasisPoints: number | null;
  totals: RevenueTotals;
  priorTotals: RevenueTotals | null;
  trend: TrendPoint[];
  onlineNow: boolean | null;
  stale: boolean | null;
  lastSnapshotAt: string | null;
  /**
   * RM "no meter movement" detector (defense-in-depth, informational — see
   * MeterIngestProcessor): true when this machine's meters have shown no
   * movement for >= 10 min of continuous beats. Current-period only (same
   * null-on-historical gate as onlineNow/stale/lastSnapshotAt above). NOT the
   * same signal as `stale` (SC/heartbeat connectivity) — a machine can be
   * online and still show no meter movement.
   */
  metersStale: boolean | null;
  completeness: RevenueMachineCompleteness;
  dataState: DataState;
  periodToken: string;
  from: string | null;
  to: string | null;
  scopeDescription: string;
  // Every config of this cabinet with data in the window, one row each —
  // switch the drill between them; nothing is ever summed across configs.
  versions: MachineRevenueRow[];
};

/**
 * Shared TanStack Query options for the three revenue-dashboard queries the
 * pages render (portfolio/site/machine). Task D1 — the portal previously
 * only polled a "today" window and never refetched on refocus, so a tab
 * left open (or parked on a historical period) could sit stale until a hard
 * refresh. 60s aligns with the SC beat cadence — do not poll faster than
 * this. `refetchOnWindowFocus` catches the "left the tab in the background"
 * case immediately instead of waiting for the next tick.
 */
const REVENUE_LIVE_QUERY_OPTS = {
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
} as const;

/**
 * Builds the `period=…[&from=…&to=…]` query string. `from`/`to` are only
 * sent for `custom` — the backend 400s (`invalid_period`) if `custom` is
 * requested without both.
 */
function periodQs(period: string, from?: string, to?: string): string {
  const p = new URLSearchParams({ period });
  if (period === 'custom' && from && to) {
    p.set('from', from);
    p.set('to', to);
  }
  return p.toString();
}

/** {@link periodQs} plus the Revenue page filter-bar's optional operator/site
 *  drill filters (GET /api/v1/revenue/portfolio's `operatorId`/`siteId`). */
function portfolioQs(period: string, from?: string, to?: string, operatorId?: string, siteId?: string): string {
  const p = new URLSearchParams(periodQs(period, from, to));
  if (operatorId) p.set('operatorId', operatorId);
  if (siteId) p.set('siteId', siteId);
  return p.toString();
}

export function useRevenuePortfolio(
  period: string, from?: string, to?: string, operatorId?: string, siteId?: string,
) {
  const getToken = useAccessToken();
  const enabled = period !== 'custom' || (!!from && !!to);
  return useQuery({
    queryKey: ['revenue', 'portfolio', period, from ?? null, to ?? null, operatorId ?? null, siteId ?? null],
    queryFn: () =>
      apiFetch<RevenuePortfolio>(
        `/api/v1/revenue/portfolio?${portfolioQs(period, from, to, operatorId, siteId)}`, {}, getToken),
    enabled,
    ...REVENUE_LIVE_QUERY_OPTS,
  });
}

/** One operator visible to the caller under RLS (Revenue filter-bar dropdown). */
export type RevenueScopeOperator = { operatorId: string; displayName: string };

/** One site visible to the caller under RLS, tagged with its operator so the
 *  portal can cascade the site dropdown to the selected operator client-side. */
export type RevenueScopeSite = { siteId: string; siteName: string; operatorId: string };

export type RevenueScope = { operators: RevenueScopeOperator[]; sites: RevenueScopeSite[] };

/**
 * The Revenue page filter bar's data source — every operator/site the caller
 * can see under RLS, gated by revenue.dashboard.view ALONE (not sites.view /
 * platform.operators.read — a revenue-only viewer like ShareholderViewer must
 * be able to populate the dropdowns without those separate admin
 * permissions). `staleTime` matches the other reference-data hooks in this
 * file (operator/site membership changes rarely, unlike the live revenue
 * numbers) — no need to poll it on the same 60s cadence as the money queries.
 */
export function useRevenueScope() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['revenue', 'scope'],
    queryFn: () => apiFetch<RevenueScope>('/api/v1/revenue/scope', {}, getToken),
    staleTime: 60_000,
  });
}

export function useRevenueSite(siteId: string | null, period: string, from?: string, to?: string) {
  const getToken = useAccessToken();
  const enabled = !!siteId && (period !== 'custom' || (!!from && !!to));
  return useQuery({
    queryKey: ['revenue', 'site', siteId, period, from ?? null, to ?? null],
    queryFn: async () => {
      try {
        return await apiFetch<RevenueSite>(
          `/api/v1/revenue/sites/${siteId}?${periodQs(period, from, to)}`, {}, getToken);
      } catch (e) {
        if ((e as ApiError).status === 404) return null;
        throw e;
      }
    },
    enabled,
    ...REVENUE_LIVE_QUERY_OPTS,
  });
}

// ---------------------------------------------------------------------------
// Suspect meter-day review workflow.
// ---------------------------------------------------------------------------

/** One suspect meter-day awaiting (or already given) review. Field shapes
 *  mirror SuspectDayEndpoints.SuspectDayRow.ToDto() verbatim. */
export type SuspectDay = {
  id: string;
  machineId: string;
  machineLabel: string;
  siteId: string;
  siteName: string;
  gamingDay: string; // YYYY-MM-DD
  versionNo: number;
  gameName: string | null;
  denominationCents: number | null;
  // NULL on a baseline day (the delta view short-circuits money to null).
  turnoverCents: number | null;
  totalOutCents: number | null;
  jackpotsCents: number | null;
  // Why the engine flagged the day suspect (e.g. 'gap_implausible',
  // 'meter_regression', ...). NULL for rows that pre-date the reason column —
  // the UI shows "reason not recorded".
  suspectReason: string | null;
  // null until reviewed; then the review timestamp + reviewer display name.
  suspectReviewedAt: string | null;
  suspectReviewedByName: string | null;
};

/** One day's raw cumulative 33 meters + its per-day flags, for the side-by-side
 *  Detail view. `meters` is keyed by the DB meter-column name (schema-1 wire
 *  order); values are credit counts (integers), NOT cents. */
export type SuspectDayDetailDay = {
  gamingDay: string; // YYYY-MM-DD
  versionNo: number;
  isBaselineDay: boolean;
  ramResetDay: boolean;
  suspect: boolean;
  suspectReason: string | null;
  meters: Record<string, number>;
};

/** Previous / suspect / next day side-by-side for a suspect meter-day.
 *  previousDay / nextDay are null when the machine has no earlier / later day.
 *  Field shapes mirror SuspectDayEndpoints.Detail verbatim. */
export type SuspectDayDetail = {
  machineId: string;
  machineLabel: string;
  siteName: string;
  gameName: string | null;
  denominationCents: number | null;
  suspectDay: SuspectDayDetailDay;
  previousDay: SuspectDayDetailDay | null;
  nextDay: SuspectDayDetailDay | null;
};

export type SuspectResolution = 'good' | 'suspect';

export type ResolveSuspectDayResult = {
  id: string;
  suspect: boolean;
  suspectReviewedAt: string | null;
  suspectReviewedByName: string | null;
};

export type SuspectDaysQueryOpts = {
  siteId?: string | null;
  machineId?: string | null;
  enabled?: boolean;
};

function suspectDaysQs(period: string, from?: string, to?: string, siteId?: string | null, machineId?: string | null): string {
  const p = new URLSearchParams({ period });
  if (period === 'custom' && from && to) {
    p.set('from', from);
    p.set('to', to);
  }
  if (siteId) p.set('siteId', siteId);
  if (machineId) p.set('machineId', machineId);
  return p.toString();
}

/** Lists the suspect meter-days in scope + period (revenue.dashboard.view). */
export function useSuspectDays(period: string, from?: string, to?: string, opts?: SuspectDaysQueryOpts) {
  const getToken = useAccessToken();
  const siteId = opts?.siteId ?? null;
  const machineId = opts?.machineId ?? null;
  const enabled = (opts?.enabled ?? true) && (period !== 'custom' || (!!from && !!to));
  return useQuery({
    queryKey: ['revenue', 'suspect-days', period, from ?? null, to ?? null, siteId, machineId],
    queryFn: () =>
      apiFetch<SuspectDay[]>(
        `/api/v1/revenue/suspect-days?${suspectDaysQs(period, from, to, siteId, machineId)}`, {}, getToken),
    enabled,
  });
}

/** Resolves one suspect meter-day — "good" clears the flag, "suspect" keeps it
 *  flagged but marks it reviewed (meters.audit-ack). On success invalidates the
 *  suspect-days list AND every revenue query so the dashboard's red
 *  dots/flagged count refresh. */
export function useResolveSuspectDay() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: SuspectResolution }) =>
      apiFetch<ResolveSuspectDayResult>(`/api/v1/revenue/suspect-days/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution }),
      }, getToken),
    onSuccess: () => {
      // Both the suspect-days list and the aggregate revenue views key off
      // ['revenue', ...], so a single prefix invalidation refreshes the dialog
      // list and the dashboard flagged-count / red trend markers together.
      void qc.invalidateQueries({ queryKey: ['revenue'] });
    },
  });
}

/** Fetches the previous/suspect/next day 33-meter side-by-side detail for one
 *  suspect meter-day (revenue.dashboard.view). Enabled only when `id` is set
 *  (i.e. the detail dialog is open for a specific row). */
export function useSuspectDayDetail(id: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['revenue', 'suspect-day-detail', id],
    queryFn: () =>
      apiFetch<SuspectDayDetail>(`/api/v1/revenue/suspect-days/${id}/detail`, {}, getToken),
    enabled: !!id,
  });
}

export function useRevenueMachine(
  machineId: string | null, period: string, from?: string, to?: string,
  // Optional CONFIG drill — scope the report to one game version of the
  // cabinet (default: the current one). Never a cross-config sum.
  versionId?: string | null,
) {
  const getToken = useAccessToken();
  const enabled = !!machineId && (period !== 'custom' || (!!from && !!to));
  return useQuery({
    queryKey: ['revenue', 'machine', machineId, versionId ?? null, period, from ?? null, to ?? null],
    queryFn: async () => {
      try {
        const vq = versionId ? `&versionId=${versionId}` : '';
        return await apiFetch<RevenueMachine>(
          `/api/v1/revenue/machines/${machineId}?${periodQs(period, from, to)}${vq}`, {}, getToken);
      } catch (e) {
        if ((e as ApiError).status === 404) return null;
        throw e;
      }
    },
    enabled,
    ...REVENUE_LIVE_QUERY_OPTS,
  });
}
